import {isArray, isPlainObject, clone} from 'remeda'
import {match} from 'ts-pattern'
import {entries} from './collection'
import {JsonObject} from './type'

type GenericObject = Record<string, unknown>

// https://javascript.plainenglish.io/advanced-typescript-type-level-nested-object-paths-7f3d8901f29a
/**
 * TypeFromPath
 * Get the type of the element specified by the path
 * @example
 * type TypeOfAB = TypeFromPath<{ a: { b: { c: string } }, 'a.b'>
 * // { c: string }
 */
type TypeFromPath<
  T extends GenericObject,
  Path extends string // Or, if you prefer, NestedPaths<T>
> = {
  [K in Path]: K extends keyof T
    ? T[K]
    : K extends `${infer P}.${infer S}`
    ? T[P] extends GenericObject
      ? TypeFromPath<T[P], S>
      : never
    : never
}[Path]

//https://www.typescriptlang.org/play?ts=4.2.3&ssl=35&ssc=1&pln=31&pc=1#code/C4TwDgpgBAUg9gSwHYB4DSAaKAFAfFAXijSggA9gIkATAZyluACdkBzKAHyiQFcBbAEYQmUAPwBYAFBQZOUhSp0GzNp278hIidNkADACQBvNAF8jAInPzKNetjFRLUAFyOAdObOHsJ3VNku3BAAbsKBSCHCANxSsZKgkDhMIYRQANoRoUxYAAxYAIxYAExYAMxYACxYAKxYAGxYAOxYABxYAJwFef6y+YVQ+SUD5QNVA7UDDQPNA20DnVBFeVBuqzlpALobcQnQ2ACGwAAWtCgAKlgAItaK9LyCYUT5OfhEaZcbN7bpmcKfokEsoEzl8lHABAArCAAY2AYh6MkM6RIyCgAGsICA4AAzKBnDYAWlErhI5BsSkYLCQ7C49008J0ARkBmMvjU8GQ6CwB2OpzOaTQG25yWC7w2uFwCKZrl+TClJjSGKxuPxgUsO3A0AAMhB9qE+VdQXcNI8Bi9UmKjT9Ikx-oCwq4QWTblBwVDYQyAkiBVBUUqcXjCcTYIhUJgoDq9RA+QKhUkQmKJVAFf6VZ9XJYYpINYkAHLRyjUADykJhwDOmtShil+1clLYWYCAjrKmpjdkEUYrmrjKZ0JbVNY7ZkJmHruOwnzXagPaZsn7ykHY9HUhX2filanhZL7uAPJOqX3py3EGLpdhFcguCzAHob1BdlAT2fd0fUuZ9lYuOYBF-HJ3gD-cw4AnJgtyAgC3GhICQKOScCyg8wpEfZ8dzLSN9VSDDoxQVDz3LTVJUkO8H03AtTzQ2FsPoIgPyA381HMSDoMY2D4MYRC4mQSgmGxfZoWgM5kmgWdZAAGwgbFgEdYSMClFhWCOaS8VkqVqEOWtFzYVc4hIx8hIgCBqKw3V9XOYTrygEjaDgJgmBALBoTgPgwAQCSREAXg3AG6dwBsYg8wBqPakPTKwMozTOjd91OAT9GIkqS3CimLvwUpSEo0oC4uANxMrS6KvyC+9zBylKssSoCSuyyTSvSxiKoqsrYqqyr4pyhquAKxwcuKhBFOqvLGviirWpqrhVjcM1BigPhbOgMa1A68w6p61Klt6txVpW5aso2vrPxzQThOMohqPMwyymvDr9MO8KaJkcw2s6pqHsWrbcqSx6WqekaoAW7q1ueoavv65LXvq9L9qgABZEAAHEqGEBBoUvCBzitN0y1eGcpSVWhXAAQTs-YQBQI9zglLM1ykJykEYB8C1caG4YiFgkc1XDyJfMtkcx0SZBx1w0jorAmIQ6DtkkEwpCAA

export type Paths<T> =
  | {
      [K in keyof T]: K extends string | number
        ? T[K] extends object
          ? `${K}` | {[L in keyof T[K] & string]: `${K}.${L}`}[keyof T[K] & string]
          : `${K}`
        : never
    }[keyof T]

export type DeltaValues<T extends GenericObject> = {
  [P in Paths<T>]?: TypeFromPath<T, P>
}

export const pathValue = <T extends GenericObject, Path extends Paths<T>>(
  values: T | undefined,
  path: Path
): TypeFromPath<T, Path> | undefined => {
  if (values == null) return undefined
  return values == null ? undefined : pathValueDynamic(values, path?.split('.') ?? [])
}

export const pathValueDynamic = (values: GenericObject, path: string[]): any => {
  let value: GenericObject | Array<unknown> | undefined = values
  const tokens = [...path]
  while (tokens.length > 0) {
    if (value == null) return undefined
    const token = tokens.shift()!
    value = match(value)
      .when(isPlainObject, v => v[token] as GenericObject)
      .when(isArray, v => v[token as any] as Array<unknown>)
      .otherwise(() => undefined)
  }
  return value
}

export type DeltaValuesDynamic = {[key: string]: any}

export const withDeltaValues = (o: JsonObject, values: DeltaValuesDynamic) => {
  let res = {...o}
  entries(values as GenericObject).forEach(([path, value]) => {
    res = withDeltaValue(res, path, value)
  })
  return res
}

/** returns the given o with value set on the given path delimited by '.'. Creates objects along the path if needed */
const withDeltaValue = (o: JsonObject, path: string, value: any): JsonObject => {
  const keys = path.split('.')
  const result: JsonObject = {...o} // Shallow clone of the top-level object
  let parent = result
  keys.forEach((key, index) => {
    if (parent == null) return
    if (index === keys.length - 1) parent[key] = value
    else {
      const child = parent[key]
      if (child === undefined) parent[key] = {}
      else if (typeof child != 'object') throw new Error('Intermediate objects in the path must be objects')
      else parent[key] = {...child}
      parent = parent[key] as JsonObject
    }
  })
  return result
}
